/**
 * @file Application root: boots the app-wide listeners, declares every route, and mounts the
 * tree inside the store, the theme and the router. Owns no failure path.
 */
import "../styles/styles";
import "./Casadesk/Browser/CapturedURLParams";
import * as React from "react";
import * as ReactDOM from "react-dom";
import {
  Router,
  Route,
  Redirect,
  IndexRoute,
  browserHistory,
} from "react-router";
import { Provider } from "react-redux";
import Store from "./Casadesk/Store/Store";
import Version from "./Casadesk/Version/Version";
import VersionSignal from "./Casadesk/Version/VersionSignal";
import History from "./Casadesk/Browser/History";
import Constants from "./Casadesk/Action/Constants";
import { firstTasksToFinish } from "./Casadesk/Saga/Sequences/AuthSequence";
import { tasks } from "./Casadesk/Saga/Sequences/AuthSequence";
import { MuiThemeProvider, createMuiTheme } from "@material-ui/core/styles";

// Layout
import Layout from "./Casadesk/Component/Layout";

// Auth
import Signup from "./Casadesk/Component/Auth/Signup/Signup";
import CheckEmail from "./Casadesk/Component/Auth/CheckEmail/CheckEmail";
import Confirm from "./Casadesk/Component/Auth/Confirm/Confirm";
import Invite from "./Casadesk/Component/Auth/Invite/Invite";
import ForgotPassword from "./Casadesk/Component/Auth/ForgotPassword/ForgotPassword";
import ConfirmEmail from "./Casadesk/Component/Auth/Email/ConfirmEmail";
import Unsubscribe from "./Casadesk/Component/Auth/Email/Unsubscribe";

// Contacts
import Persons from "./Casadesk/Component/Pages/Contact/Persons";
import Companies from "./Casadesk/Component/Pages/Contact/Companies";

// Person
import PersonCreate from "./Casadesk/Component/Pages/Contact/PersonCreate";
import PersonUpdate from "./Casadesk/Component/Pages/Contact/PersonUpdate";

// Company
import CompanyCreate from "./Casadesk/Component/Pages/Contact/CompanyCreate";
import CompanyUpdate from "./Casadesk/Component/Pages/Contact/CompanyUpdate";

// Unit
import Units from "./Casadesk/Component/Pages/Property/Units";
import UnitCreate from "./Casadesk/Component/Pages/Property/UnitCreate";
import UnitUpdate from "./Casadesk/Component/Pages/Property/UnitUpdate";
import UnitDefaultChargeTemplates from "./Casadesk/Component/Pages/Property/UnitDefaultChargeTemplates";

// Property
import Properties from "./Casadesk/Component/Pages/Property/Properties";
import PropertyCreate from "./Casadesk/Component/Pages/Property/PropertyCreate";
import PropertyUpdate from "./Casadesk/Component/Pages/Property/PropertyUpdate";

// Contract
import ContractCreate from "./Casadesk/Component/Pages/Contract/ContractCreate";
import ContractUpdate from "./Casadesk/Component/Pages/Contract/ContractUpdate";
import ContractExtend from "./Casadesk/Component/Pages/Contract/ContractExtend";

// Charge
import ChargeUpdate from "./Casadesk/Component/Pages/Charge/ChargeUpdate";

// Payment
import PaymentUpdate from "./Casadesk/Component/Pages/Payment/PaymentUpdate";

// Dashboard
import Overview from "./Casadesk/Component/Pages/Dashboard/Overview";
// import Receivables from "./Casadesk/Component/Pages/Dashboard/Receivables";
import Charge from "./Casadesk/Component/Pages/Dashboard/Charges";
// import Payables from "./Casadesk/Component/Pages/Dashboard/Payables";
import Notifications from "./Casadesk/Component/Pages/Dashboard/Notifications";
import Activities from "./Casadesk/Component/Pages/Dashboard/Activities";
import Contracts from "./Casadesk/Component/Pages/Dashboard/Contracts";
import Tasks from "./Casadesk/Component/Pages/Dashboard/Tasks";
import Notes from "./Casadesk/Component/Pages/Dashboard/Notes";
import Statements from "./Casadesk/Component/Pages/Dashboard/Statements";
import Unused from "./Casadesk/Component/Pages/Dashboard/Unused";
import Messages from "./Casadesk/Component/Pages/Dashboard/Messages";

// Mails
import Mails from "./Casadesk/Component/Pages/Mails/Mails";
import MailDetailPage from "./Casadesk/Component/Pages/Mails/MailDetailPage";

// Settings
import Settings from "./Casadesk/Component/Pages/Settings/Settings";

// Finance
import Charges from "./Casadesk/Component/Pages/Finance/Charges";
import Payments from "./Casadesk/Component/Pages/Finance/Payments";
import BankTransactions from "./Casadesk/Component/Pages/Finance/BankTransactions";
import OwnerStatements from "./Casadesk/Component/Pages/Finance/OwnerStatements";
import OwnersReports from "./Casadesk/Component/Pages/Finance/OwnersReports";
import Savings from "./Casadesk/Component/Pages/Finance/Savings";
import Reports from "./Casadesk/Component/Pages/Finance/Reports";
import Invoices from "./Casadesk/Component/Pages/Finance/Invoices";
import AgencyStatements from "./Casadesk/Component/Pages/Finance/AgencyStatements";

import AgencyBalance from "./Casadesk/Component/Pages/Finance/AgencyBalance";

// Navigation
import { Navigation } from "./Casadesk/Component/Partials/Navigation/Navigation";

// Partials
import { Redirector } from "./Casadesk/Component/Partials/Redirector/Redirector";
import { NotFound } from "./Casadesk/Component/Partials/Error/NotFound";
import Information from "./Casadesk/Component/Partials/Information/Information";
import Spinner from "./Casadesk/Component/Partials/Spinner/Spinner";

// Financial Statements
import FinancialStatementUpdate from "./Casadesk/Component/Pages/FinancialStatement/FinancialStatementUpdate";

// Events
import NotificationUpdate from "./Casadesk/Component/Pages/Event/NotificationUpdate";

// Activity
import ActivityTemplateCreate from "./Casadesk/Component/Pages/Activity/ActivityTemplateCreate";
import ActivityTemplateUpdate from "./Casadesk/Component/Pages/Activity/ActivityTemplateUpdate";
import ActivityCreate from "./Casadesk/Component/Pages/Activity/ActivityCreate";
import ActivityUpdate from "./Casadesk/Component/Pages/Activity/ActivityUpdate";

// User
import UserCreate from "./Casadesk/Component/Pages/User/UserCreate";
import UserUpdate from "./Casadesk/Component/Pages/User/UserUpdate";

// Transaction File
import BankTransactionFileUpdate from "./Casadesk/Component/Pages/BankTransactionFile/BankTransactionFileUpdate";

// Task
import TaskCreate from "./Casadesk/Component/Pages/Task/TaskCreate";
import TaskUpdate from "./Casadesk/Component/Pages/Task/TaskUpdate";

// Notes
import NoteCreate from "./Casadesk/Component/Pages/Note/NoteCreate";
import NoteUpdate from "./Casadesk/Component/Pages/Note/NoteUpdate";

// Email
import EmailSend from "./Casadesk/Component/Pages/Email/EmailSend";
import LinkSend from "./Casadesk/Component/Pages/Email/LinkSend";

// Conversation
import ConversationUpdate from "./Casadesk/Component/Pages/Conversation/ConversationUpdate";

// Agreement
import AgreementUpdate from "./Casadesk/Component/Pages/Agreement/AgreementUpdate";

// Key switch, hidden URL, only for client
import KeySwitch from "./Casadesk/Component/Pages/KeySwitch/KeySwitch";

// new
import CoachNewBillSettlement from "./Casadesk/Component/Pages/Property/CoachNewBillSettlement/CoachNewBillSettlement";

class RootComponent extends React.Component<any, any> {
  protected interval: any;
  protected entitiesToFetch: number = [...firstTasksToFinish, ...tasks].length;
  protected isLoaded: boolean = false;

  public state = {
    isLoggedIn: false,
  };

  constructor(public props) {
    super(props);

    this.boot();
  }

  public boot(): void {
    // At most one of the two passes its gate; both return early on a dev server.
    Version.listen();
    VersionSignal.listen();
    // Listen browser history
    setTimeout(() => {
      History.listen(browserHistory);
    });
  }

  public componentWillMount() {
    /*
    |  @todo
    |  We cannot access react router here as it is not loaded yet, but we have to disable
    |  token protection for pages like confirm.
    */
    if (!(window as any).location.href.includes("?token")) {
      Store.getConfigurator().dispatch({
        type: Constants.TOKEN_CHECK,
        onSuccess: (isLoggedIn: boolean) => {
          if (isLoggedIn) {
            this.copyHistory();
          }

          this.setState(
            {
              isLoggedIn: isLoggedIn,
            },
            this.startInterval
          );
        },
      });
    } else {
      this.setState(
        {
          isLoggedIn: false,
        },
        this.startInterval
      );
    }

    if (window.location?.search === "?trans") {
      Store.getConfigurator().dispatch({
        type: Constants.ACTIVATE_CLIENT_ACCOUNT,
        payload: true,
      });
    }
  }

  public copyHistory = () => {
    const history =
      sessionStorage.getItem("history") &&
      [...JSON.parse(sessionStorage.getItem("history"))].reverse();

    if (history && history.length > 0) {
      if (history[history.length - 1] !== location.pathname) {
        browserHistory.push(location.pathname);
      } else {
        for (let i = 0; i < history.length; i++) {
          setTimeout(() => {
            browserHistory.push(history[i]);
          }, 10);
        }
      }
    }
  };

  public startInterval(): any {
    /*
    |  @todo refactor this ugly part of our application
    |  Use HoC in Layout.
    */

    // Wait until we fetch all the major entities from backend.
    // this part is not clear enough
    this.interval = setInterval(() => {
      if (!this.state.isLoggedIn) {
        sessionStorage.clear();

        this.isLoaded = true;
        this.forceUpdate();
        clearInterval(this.interval);
      }

      const length =
        Store.getConfigurator().getState().app.loadedEntities.length;

      if (length === this.entitiesToFetch) {
        clearInterval(this.interval);
        this.isLoaded = true;
        this.forceUpdate();
      }
    }, 300);
  }

  public render() {
    // Do not render router before we fetch entities from backend.
    if (this.isLoaded === false) {
      return (
        <div className="preloader">
          <h1>Casadesk</h1>
          <h3>
            Loading
            <div className="loader-dots">
              <div className="dot dot1"></div>
              <div className="dot dot2"></div>
              <div className="dot dot3"></div>
              <div className="dot dot4"></div>
            </div>
          </h3>
        </div>
      );
    }

    return (
      <Router history={browserHistory}>
        <Route
          path="/app/coach/new-bill-settlement"
          component={CoachNewBillSettlement}
        />
        <Route path="/" component={Layout}>
          <IndexRoute component={Redirector} />

          <Route path="auth">
            <Route path="check-email" component={CheckEmail} />
            <Route path="signup" component={Signup} />
            <Route path="confirm" component={Confirm} />
            <Route path="confirm-invite" component={Invite} />
            <Route path="forgot-password" component={ForgotPassword} />
            <Route path="email-change-confirm" component={ConfirmEmail} />
            <Route path="email-unsubscribe" component={Unsubscribe} />
          </Route>

          <Route path="app">
            <Route path="properties" component={Properties} />
            <Route path="mails" component={Mails} />
            <Route path="units" component={Units} />
            <Route path="navigation/:path" component={Navigation} />
            <Route path="companies" component={Companies} />

            <Route path="contacts">
              <Route path="persons" component={Persons} />
              <Route path="companies" component={Companies} />
            </Route>

            <Route path="owner-statement">
              <Route path="update/:id" component={FinancialStatementUpdate} />
            </Route>

            <Route path="agency-statement">
              <Route path="update/:id" component={FinancialStatementUpdate} />
            </Route>

            <Route path="person">
              <Route path="create" component={PersonCreate} />
              <Route path="update/:id(/:companyId)" component={PersonUpdate} />
            </Route>

            <Route path="tenant">
              <Route path="create" component={PersonCreate} />
              <Route path="update/:id(/:companyId)" component={PersonUpdate} />
            </Route>

            <Route path="executor">
              <Route path="create" component={PersonCreate} />
              <Route path="update/:id(/:companyId)" component={PersonUpdate} />
            </Route>

            <Route path="caretaker">
              <Route path="create" component={PersonCreate} />
              <Route path="update/:id(/:companyId)" component={PersonUpdate} />
            </Route>

            <Route path="property">
              <Route path="create(/:ownerId)" component={PropertyCreate} />
              <Route path="update/:id" component={PropertyUpdate} />
            </Route>

            <Route path="unit">
              <Route path="create(/:propertyId)" component={UnitCreate} />
              <Route path="update/:id" component={UnitUpdate} />
              <Route
                path=":id/default-charge-templates"
                component={UnitDefaultChargeTemplates}
              />
            </Route>

            {/* <Route path="coach">
              <Route
                path="new-bill-settlement"
                component={CoachNewBillSettlement}
              />
            </Route> */}

            <Route path="company">
              <Route path="create" component={CompanyCreate} />
              <Route path="update/:id" component={CompanyUpdate} />
            </Route>

            <Route path="owner">
              <Route path="create" component={CompanyCreate} />
              <Route path="update/:id" component={CompanyUpdate} />
            </Route>

            <Route path="agency">
              <Route path="update/:id" component={CompanyUpdate} />
            </Route>

            <Route path="contract">
              <Route
                path="create(/:personId)(/:unitId)"
                component={ContractCreate}
              />
              <Route
                path="update/:id(/:personId)(/:unitId)"
                component={ContractUpdate}
              />
              <Route path="extend/:id" component={ContractExtend} />
            </Route>

            <Route path="charge">
              <Route path="update/:id" component={ChargeUpdate} />
            </Route>

            <Route path="invoice">
              <Route path="update/:id" component={ChargeUpdate} />
            </Route>

            <Route path="payment">
              <Route path="update/:id" component={PaymentUpdate} />
            </Route>

            <Route path="settings" component={Settings} />

            <Route path="finance">
              <Route path="charge" component={Charges} />
              <Route path="payment" component={Payments} />
              <Route path="transaction" component={BankTransactions} />
              <Route path="owner-statements" component={OwnerStatements} />
              <Route path="owner-statements/:id" component={OwnersReports} />
              <Route path="agencybalance" component={AgencyBalance} />
              <Route path="savings" component={Savings} />
              <Route path="reports" component={Reports} />
              <Route path="invoices" component={Invoices} />
              <Route path="agency-statements" component={AgencyStatements} />
            </Route>

            <Route path="transaction-file">
              <Route path=":fileId" component={BankTransactionFileUpdate} />
            </Route>

            <Route path="dashboard">
              <Route path="overview" component={Overview} />
              {/* <Route path="receivables" component={Receivables} />
              <Route path="payables" component={Payables} /> */}
              <Route path="charges" component={Charge} />
              <Route path="notifications" component={Notifications} />
              <Route path="activities" component={Activities} />
              <Route path="contracts" component={Contracts} />
              <Route path="tasks" component={Tasks} />
              <Route path="notes" component={Notes} />
              <Route path="statements" component={Statements} />
              <Route path="unused" component={Unused} />
              <Route path="messages" component={Messages} />
            </Route>

            <Route path="mails/:id">
              <Route path="mail/:id" component={MailDetailPage} />
            </Route>

            <Route path="notification">
              <Route path="update/:id" component={NotificationUpdate} />
            </Route>

            <Route path="new" component={Information} />

            <Route path="activity-template">
              <Route path="create" component={ActivityTemplateCreate} />
              <Route path="update/:id" component={ActivityTemplateUpdate} />
            </Route>

            <Route path="activity">
              <Route
                path="create/:activityTemplateId(/:propertyId)(/:unitId)"
                component={ActivityCreate}
              />
              <Route path="update/:id" component={ActivityUpdate} />
            </Route>

            <Route path="user">
              <Route path="create" component={UserCreate} />
              <Route path="update/:id" component={UserUpdate} />
            </Route>

            <Route path="task">
              <Route path="create" component={TaskCreate} />
              <Route path="update/:id" component={TaskUpdate} />
            </Route>

            <Route path="note">
              <Route path="create" component={NoteCreate} />
              <Route path="update/:id" component={NoteUpdate} />
            </Route>

            <Route path="email">
              <Route path="send/:entity(/:id)" component={EmailSend} />
            </Route>

            <Route path="link">
              <Route path="send/:entity/:id" component={LinkSend} />
            </Route>

            <Route path="conversation">
              <Route path="update/:id" component={ConversationUpdate} />
            </Route>

            <Route path="agreement">
              <Route path="update/:id" component={AgreementUpdate} />
            </Route>

            <Route path="translations" component={KeySwitch} />
          </Route>

          <Route path="404" component={NotFound} />
          <Redirect from="*" to="404" />
        </Route>
      </Router>
    );
  }
}

const theme = createMuiTheme({
  typography: {
    fontSize: 14,
  },
  palette: {
    primary: {
      main: "#00BCD4",
      contrastText: "#ffffff",
    },
    secondary: {
      main: "#f44336",
    },
  },
  overrides: {
    MuiFormControlLabel: {
      label: {
        fontFamily: "Roboto, sans-serif !important",
      },
    },
  },
});

ReactDOM.render(
  <Provider store={Store.getConfigurator()}>
    <React.Fragment>
      <div>
        <MuiThemeProvider theme={theme}>
          <RootComponent />
        </MuiThemeProvider>
      </div>
      <Spinner />
    </React.Fragment>
  </Provider>,
  document.getElementById("app")
);
